
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/auth';
import { toast } from '@/hooks/use-toast';
import { SubscriptionContext } from './SubscriptionContext';
import { SubscriptionProviderProps, SubscriptionTier } from './types';
import { canAccessFeature } from './subscription-utils';
import { supabase } from '@/integrations/supabase/client';
import { subscriptionsTable } from '@/integrations/supabase/compat';
import { fetchJson } from '@/lib/api-client';
import { fetchSubscriptionStatus } from '@/lib/subscription-status-request';
import { getStripe } from '@/lib/stripe';
import { logger } from '@/lib/environment';
import { trackEvent } from '@/lib/analytics';
import { trackMetaEvent } from '@/lib/meta-pixel';
import { readMetaCookies } from '@/lib/meta-cookies';
import type { Tables } from '@/integrations/supabase/types';

/** A refocus refetches server status only when the last fetch is at least this old. */
export const SERVER_REFRESH_MIN_MS = 60_000;

export function SubscriptionProvider({ children }: SubscriptionProviderProps) {
  const { user, session, subscriptionTier, isSubscribed } = useAuth();
  // Key everything on the user's id, not the User object: supabase-js hands out
  // a new object on every auth event/getSession, and keying on it refetched
  // get-subscription-status several times per refocus (03-10 production).
  const userId = user?.id ?? null;
  const hasAccessToken = Boolean(session?.access_token);
  const [isLoading, setIsLoading] = useState(false);
  const checkingRef = useRef(false);
  // Launch contract: this provider keeps UI state in sync, but server-side route
  // checks remain authoritative for premium-only access decisions.
  const [serverResolved, setServerResolved] = useState<{ loaded: boolean; tier: SubscriptionTier; isSubscribed: boolean }>({ loaded: false, tier: (subscriptionTier as SubscriptionTier) || 'free', isSubscribed });

  // Function to check subscription status - stabilized with useCallback
  const checkSubscriptionStatus = useCallback(async () => {
    if (!userId || checkingRef.current) {
      return;
    }

    try {
      checkingRef.current = true;
      setIsLoading(true);
      
      // Client-side sanity check without joins (avoid RLS surprises)
      const { data: subscription, error } = await subscriptionsTable()
        .select('*')
        .eq('user_id', userId)
        .in('status', ['active', 'trialing'])
        .order('created_at', { ascending: false })
        .maybeSingle();

      if (error && (error as { code?: string }).code !== 'PGRST116') {
        logger.error('Error checking subscription status', { error: (error as { message?: string }).message });
      }

      if (subscription) {
        const sub = subscription as { status?: string; updated_at?: string };
        const tier = (sub.status === 'active' || sub.status === 'trialing') ? 'premium' : 'free';
        logger.debug('Active subscription found (client-check)', { tier, userId });
        setServerResolved({ loaded: true, tier: tier as SubscriptionTier, isSubscribed: tier === 'premium' });
      } else {
        logger.debug('No active subscription found for user (client-check)', { userId });
        setServerResolved({ loaded: true, tier: 'free', isSubscribed: false });
      }

      setIsLoading(false);
    } catch (error) {
      logger.error('Error checking subscription', { error: error instanceof Error ? error.message : 'Unknown error' });
      setIsLoading(false);
    } finally {
      checkingRef.current = false;
    }
  }, [userId]); // keyed on the id; the ref prevents overlapping checks

  // Check subscription status when user changes - only run once per user
  useEffect(() => {
    if (userId) {
      checkSubscriptionStatus();
    }
  }, [userId, checkSubscriptionStatus]);

  // Server-evaluated subscription status (edge function, schema-validated).
  // Fetched when the signed-in user changes (sign-in, user switch, page load —
  // which covers the return from Stripe checkout/portal), and again after a
  // successful auth refocus when the last fetch is older than
  // SERVER_REFRESH_MIN_MS (tab return after a real absence). Concurrent calls
  // share one request with UnifiedAuthProvider's (subscription-status-request).
  const lastServerFetchRef = useRef<{ userId: string; at: number } | null>(null);
  const fetchServerStatus = useCallback(async (forUserId: string, isCancelled: () => boolean) => {
    lastServerFetchRef.current = { userId: forUserId, at: Date.now() };
    try {
      const data = await fetchSubscriptionStatus(forUserId);
      if (isCancelled()) return;
      const nextTier: SubscriptionTier = data.isSubscribed ? 'premium' : 'free';
      setServerResolved({ loaded: true, tier: nextTier, isSubscribed: data.isSubscribed });
    } catch (err) {
      logger.warn('get-subscription-status check failed', { err: err instanceof Error ? err.message : String(err) });
      // Do not change resolved state on failure
    }
  }, []);

  useEffect(() => {
    // Only call server when a valid session with access token is present
    if (!userId || !hasAccessToken) return;
    let cancelled = false;
    void fetchServerStatus(userId, () => cancelled);
    const onRefocusSuccess = () => {
      const last = lastServerFetchRef.current;
      if (last && last.userId === userId && Date.now() - last.at < SERVER_REFRESH_MIN_MS) return;
      void fetchServerStatus(userId, () => cancelled);
    };
    window.addEventListener('auth:refocus:success', onRefocusSuccess);
    return () => {
      cancelled = true;
      window.removeEventListener('auth:refocus:success', onRefocusSuccess);
    };
  }, [userId, hasAccessToken, fetchServerStatus]);

  // Function to start a checkout session
  const startCheckoutSession = async (tier: 'premium') => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to subscribe",
        variant: "destructive"
      });
      return;
    }

    if (!tier || tier !== 'premium') {
      toast({
        title: "Invalid plan",
        description: "Please select a valid subscription plan",
        variant: "destructive"
      });
      return;
    }

    let safetyTimer: number | undefined;
    try {
      setIsLoading(true);
      safetyTimer = window.setTimeout(() => setIsLoading(false), 30_000);

      // If redirect takes time, show a gentle notification
      const delayedNotice = setTimeout(() => {
        toast({
          title: 'Redirecting to Stripe…',
          description: 'Please wait while we start your secure checkout.',
        });
      }, 500);

      // Generate a single event_id used for browser + CAPI dedup. fbp/fbc
      // are read at click time and propagated through Stripe metadata so the
      // webhook can replay them via CAPI. trackMetaEvent no-ops without
      // marketing consent; checkoutBody fields are optional in the validator.
      const fbEventId = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
        ? crypto.randomUUID()
        : `cs_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const fbCookies = readMetaCookies();

      trackMetaEvent(
        'InitiateCheckout',
        { value: 19, currency: 'GBP', content_name: tier },
        fbEventId,
      );

      const checkoutBody: Record<string, string> = { tier };
      checkoutBody.fb_event_id = fbEventId;
      if (fbCookies.fbp) checkoutBody.fbp = fbCookies.fbp;
      if (fbCookies.fbc) checkoutBody.fbc = fbCookies.fbc;

      const { createCheckoutSessionResponseSchema } = await import('@/types/dto');
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort('timeout'), 12_000);
      const data = await fetchJson('create-checkout-session', { method: 'POST', body: JSON.stringify(checkoutBody), signal: controller.signal }, createCheckoutSessionResponseSchema);
      window.clearTimeout(timeout);
      clearTimeout(delayedNotice);

      trackEvent('checkout_initiated', { plan_name: tier, price_gbp: 19 });

      // Redirect to Stripe checkout
      window.location.href = data.url;

    } catch (error) {
      toast({
        title: 'Checkout failed',
        description: 'Could not start the subscription process',
        variant: 'destructive',
      });
      logger.error('Error starting checkout:', error);
      setIsLoading(false);
    } finally {
      if (safetyTimer) window.clearTimeout(safetyTimer);
    }
  };

  // Function to open customer portal
  const openCustomerPortal = async () => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to manage subscription",
        variant: "destructive"
      });
      return;
    }

    let safetyTimer: number | undefined;
    try {
      setIsLoading(true);
      safetyTimer = window.setTimeout(() => setIsLoading(false), 30_000);
      
      // Get current URL for return URL
      const returnUrl = window.location.origin + '/subscription';
      
      const { createCustomerPortalResponseSchema } = await import('@/types/dto');
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort('timeout'), 12_000);
      const data = await fetchJson('create-customer-portal', { method: 'POST', body: JSON.stringify({ returnUrl }), signal: controller.signal }, createCustomerPortalResponseSchema);
      window.clearTimeout(timeout);

      // Redirect to Stripe customer portal
      window.location.href = data.url;
      
    } catch (error) {
      logger.error('Error opening portal:', error);
      toast({
        title: "Portal access failed",
        description: "Could not open subscription management portal",
        variant: "destructive"
      });
      setIsLoading(false);
    } finally {
      if (safetyTimer) window.clearTimeout(safetyTimer);
    }
  };

  const checkFeatureAccess = (featureName: string): boolean => {
    return canAccessFeature(
      subscriptionTier,
      featureName
    );
  };

  const value = {
    isLoading,
    hasSubscription: serverResolved.loaded ? serverResolved.isSubscribed : isSubscribed,
    tier: serverResolved.loaded ? serverResolved.tier : ((subscriptionTier as SubscriptionTier) || 'free'),
    expiresAt: null, // This would come from enhanced auth in the future
    checkSubscriptionStatus,
    startCheckoutSession,
    openCustomerPortal,
    canAccessFeature: checkFeatureAccess
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}
